import * as React from "react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Modal } from "../../../ui/components/Modal";
import { TextInput } from "../../../ui/components/Form";
import { Switch } from "../../../ui/components/Switch";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import * as systemEventsApi from "../../lib/api/system-events";
import type { SystemEvent } from "../../lib/api/system-events";

export interface SystemEventModalProps {
  eventId: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  onSaved?: () => void;
}

export function SystemEventModal({
  eventId,
  open,
  setOpen,
  onSaved,
}: SystemEventModalProps) {
  const { accessToken } = useAuth();
  const { t } = useI18n();
  const token =
    accessToken ??
    (typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null);

  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [event, setEvent] = React.useState<SystemEvent | null>(null);
  const [label, setLabel] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [shouldLog, setShouldLog] = React.useState(true);
  const [enabled, setEnabled] = React.useState(true);
  const [subscribable, setSubscribable] = React.useState(false);

  React.useEffect(() => {
    if (!open || !token) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    systemEventsApi
      .fetchSystemEvent(token, eventId)
      .then((row) => {
        if (cancelled) return;
        setEvent(row);
        setLabel(row.label);
        setDescription(row.description ?? "");
        setShouldLog(row.shouldLog);
        setEnabled(row.enabled);
        setSubscribable(Boolean(row.subscribable));
      })
      .catch((err: any) => {
        if (cancelled) return;
        setError(err?.message || t("pro.system.events.error.loadEvent"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, token, eventId, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !event) return;
    if (!label.trim()) {
      setError(t("pro.system.events.error.labelRequired"));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await systemEventsApi.updateSystemEvent(token, event.id, {
        label: label.trim(),
        description: description.trim() ? description.trim() : null,
        shouldLog,
        enabled,
        subscribable,
      });
      onSaved?.();
      setOpen(false);
    } catch (err: any) {
      setError(err?.message || t("pro.system.events.error.save"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen} minWidth="520px" maxWidth="600px" closeOnClickOff>
      {loading ? (
        <Modal.Body>
          <Text level="body-medium" color="muted">
            {t("pro.system.events.loading")}
          </Text>
        </Modal.Body>
      ) : (
        <Modal.Form onSubmit={handleSubmit}>
          <Modal.Header>
            <Text level="h2" weight="bold">
              {t("pro.system.events.modal.edit")}
            </Text>
          </Modal.Header>
          <Modal.Body>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {error && <Callout variant="error">{error}</Callout>}

              {event?.retiredAt && (
                <Callout variant="info">{t("pro.system.events.retiredNotice")}</Callout>
              )}

              <TextInput
                label={t("pro.system.events.form.key")}
                value={event?.key ?? ""}
                disabled
              />

              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 140 }}>
                  <TextInput
                    label={t("pro.system.events.col.domain")}
                    value={event?.domain ?? ""}
                    disabled
                  />
                </div>
                <div style={{ flex: 1, minWidth: 140 }}>
                  <TextInput
                    label={t("pro.system.events.col.source")}
                    value={event?.sourceService ?? ""}
                    disabled
                  />
                </div>
                <div style={{ flex: 1, minWidth: 140 }}>
                  <TextInput
                    label={t("pro.system.events.col.historyType")}
                    value={event?.historyType ?? ""}
                    disabled
                  />
                </div>
              </div>

              <TextInput
                label={t("pro.system.events.form.labelRequired")}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={t("pro.system.events.form.labelPlaceholder")}
                disabled={saving}
              />

              <TextInput
                label={t("pro.system.events.form.description")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("pro.system.events.form.descriptionPlaceholder")}
                disabled={saving}
              />

              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                  <div>
                    <Text weight="medium">{t("pro.system.events.form.shouldLog")}</Text>
                    <Text level="body-small" color="muted">
                      {t("pro.system.events.form.shouldLogHelp")}
                    </Text>
                  </div>
                  <Switch
                    label={t("pro.system.events.form.shouldLog")}
                    hideLabel
                    showStateText={false}
                    checked={shouldLog}
                    onCheckedChange={setShouldLog}
                    disabled={saving}
                  />
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                  <div>
                    <Text weight="medium">{t("pro.system.events.form.subscribable")}</Text>
                    <Text level="body-small" color="muted">
                      {t("pro.system.events.form.subscribableHelp")}
                    </Text>
                  </div>
                  <Switch
                    label={t("pro.system.events.form.subscribable")}
                    hideLabel
                    showStateText={false}
                    checked={subscribable}
                    onCheckedChange={setSubscribable}
                    disabled={saving}
                  />
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                  <div>
                    <Text weight="medium">{t("pro.system.events.form.enabled")}</Text>
                    <Text level="body-small" color="muted">
                      {t("pro.system.events.form.enabledHelp")}
                    </Text>
                  </div>
                  <Switch
                    label={t("pro.system.events.form.enabled")}
                    hideLabel
                    showStateText={false}
                    checked={enabled}
                    onCheckedChange={setEnabled}
                    disabled={saving}
                  />
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer style={{ justifyContent: "flex-end", gap: 10 }}>
            <Button type="button" color="neutral" onClick={() => setOpen(false)} disabled={saving}>
              {t("pro.system.events.cancel")}
            </Button>
            <Button type="submit" color="primary" disabled={saving || !event}>
              {saving ? t("pro.system.events.saving") : t("pro.system.events.saveChanges")}
            </Button>
          </Modal.Footer>
        </Modal.Form>
      )}
    </Modal>
  );
}
